/**
 * The shapes the browser app holds, and the one interface every screen reads
 * them through.
 *
 * These are **view types, not wire types.** The wire is three different things
 * — relay-server's HTTP JSON, the socket's frames, and the opaque payload
 * inside a log entry that the server never decodes
 * (`relay/src/data/relaydatasource.h`, § The wire) — and a screen that knew all
 * three would have to change when any of them did. `RelayStore` is the seam,
 * exactly as `DataSource` is in the desktop app: one interface, a stub behind
 * it today and the socket behind it later, and nothing above it changes.
 *
 * ── Two rules that are types, not conventions ───────────────────────────────
 *
 * **`Message.body` is text.** Not HTML, not Markdown, not a fragment to set
 * with `innerHTML`. React escapes a string child, so rendering `{m.body}` is
 * safe and `dangerouslySetInnerHTML` is the only way to get this wrong — which
 * is why the field is named `body` and no escaped twin exists to reach for.
 * The desktop client carries a `bodyHtml` because Qt's delegate draws rich
 * text; the browser has no such need and must not grow one.
 *
 * **A guest's name is never trusted.** `MeetingParticipant.guest` is not
 * decoration: the link is the whole credential and there is no account behind
 * it, so a stranger may type any name they like. Draw one through
 * `participantLabel()` below, always. The desktop app's CLAUDE.md records the
 * failure mode — nothing throws, every surface goes on looking right, and a
 * stranger typing "Timur Inal" reads as Timur Inal in a meeting where a
 * decision gets made.
 */

// ── people ──────────────────────────────────────────────────────────────────

/** What the presence lane carries (`{t:'prs', p, n}` on the ephemeral lane). */
export type Presence = 'online' | 'away' | 'busy' | 'offline';

export interface Person {
  id: string;
  name: string;
  /** The account's username. Null for almost every account, which is ordinary
   *  and not an error — `GET /api/relay/users/by-handle/:handle` is the lookup
   *  that uses it. */
  handle?: string;
  /** Absolute, and served by the accounts service on mauvely.com — which is why
   *  the CSP names that host in `img-src`. Absent means draw initials. */
  avatarUrl?: string;
  presence: Presence;
}

// ── conversations and messages ──────────────────────────────────────────────

/**
 * `self` is the fourth kind and is not on the wire: relay-server stores the
 * self-note as a `group` of one member. The distinction is the client's,
 * because a group of one is drawn as a scratchpad rather than as a group.
 */
export type ConversationKind = 'direct' | 'group' | 'channel' | 'self';

export interface Conversation {
  id: string;
  kind: ConversationKind;
  /** For a direct conversation this is the other person's name, resolved by the
   *  store — the server's row carries an empty title for a DM. */
  title: string;
  topic: string;
  /** Account ids. The directory row is the index; `sync` is authoritative. */
  members: string[];
  ownerId: string;
  unread: number;
  /** Milliseconds since the epoch, for sorting the list. */
  lastAt: number;
  /** One plain line of the last message, for the list row. Text, like `body`. */
  preview: string;
  /** Added 2026-09-17 for the list's affordances, and optional because the
   *  stub store does not hold them yet. All three are the desktop app's own
   *  flags (`DataSource::setPinned` / `setArchived` / `setMuted`): pinned sorts
   *  to the top, archived is out of the default filter, muted silences it.
   *  Absent reads as false. */
  pinned?: boolean;
  archived?: boolean;
  muted?: boolean;
  /** Mauvely's own conversation with this account: read-only, and the only
   *  place the verified badge is drawn. Set from the server's flag and nothing
   *  else (relay-server `docs/official.md`). Absent reads as false. */
  official?: boolean;
  /** An unread message from somebody else mentions you. Cleared by reading,
   *  as the desktop's is. Absent reads as false. */
  mentioned?: boolean;
}

/**
 * A file that stays in the sender's Mauvely Cloud.
 *
 * The bytes never travel over Relay. The message carries a share link and the
 * receiver fetches from Cloud, which is what makes chat files free of a size
 * limit — the wire shape is the `f` array in the message payload
 * (`{id, s, l, r, x, m, b}`), flattened here into names a component can read.
 */
export interface Attachment {
  /** The Cloud node: the wire's `id`. */
  nodeId: string;
  /** The filename, and a stranger's text. Drawn as text, never escaped twice. */
  name: string;
  mime: string;
  bytes: number;
  /** The share link, assembled from the wire's `l` and `s`. */
  cloudUrl: string;
}

/**
 * A quoted message.
 *
 * The excerpt travels with the quote rather than being resolved on arrival,
 * because the receiver may not hold the quoted message at all — it can sit
 * below their `joined_seq`, below `availableFrom` after archival, or have been
 * deleted on their device. Both fields are sender-supplied and **display
 * only**: `senderId` is the whole of authorship.
 */
export interface Quote {
  messageId: string;
  author: string;
  excerpt: string;
}

/** What a finished call left behind. `missed` and `declined` are the same row
 *  read from the two seats — which one it is depends on who wrote it. */
export type CallOutcome = 'answered' | 'missed' | 'declined';

export interface MessageCall {
  outcome: CallOutcome;
  /** Present and meaningful only when `outcome` is `answered`. */
  durationSeconds: number;
  camera: boolean;
}

export interface MessageMeeting {
  id: string;
  title: string;
  /** Milliseconds. Absent on the wire for a meeting with no time set. */
  at?: number;
}

/**
 * The three things a transcript row can be.
 *
 * All three are ordinary log entries with the same `seq`; the payload's `t`
 * picks between them. A `callend` and a `meeting` card are both **client
 * posted** — relay-server needed no change for either, and knows about neither.
 */
export type MessageKind = 'msg' | 'callend' | 'meeting';

export interface Message {
  /** Client-minted, and the server's dedup key. A retry reuses it; generating
   *  a fresh one per attempt turns every dropped socket into a duplicate. */
  id: string;
  conversationId: string;
  /** Per conversation, 1-based and dense. Density is the whole gap mechanism —
   *  a client knows it missed something because `seq !== last + 1`. */
  seq: number;
  /** The server's session-stamped sender. Authorship, and not forgeable. */
  senderId: string;
  /** Server time, milliseconds. */
  at: number;
  /** **Text.** See this file's header. */
  body: string;
  attachments: Attachment[];
  /** The thread root this answers — the wire's `rt`. Absent for a transcript
   *  message. A reply whose root is held is drawn in the thread pane and not
   *  in the transcript; one whose root is not held is drawn in the transcript,
   *  or nobody on this device could read it. */
  replyTo?: string;
  quote?: Quote;
  /** Account ids this message mentions — the wire's `mn`. Absent when it
   *  mentions nobody. The only thing that makes an `@Name` in `body` a pill. */
  mentions?: string[];
  kind: MessageKind;
  call?: MessageCall;
  meeting?: MessageMeeting;
  /**
   * Set on your own message until the `ack` lands.
   *
   * A write applies locally and renders now — waiting for a round trip feels
   * broken on any connection worse than an office one. Absent means acked.
   */
  pending?: 'sending' | 'failed';
  /**
   * Who did not receive this, off the ack.
   *
   * Separate from `pending` rather than a third value in it, because the
   * message is not pending: it was accepted, it owns a seq, and in a group it
   * reached everybody else. Only these people did not get it, and no retry
   * changes that.
   */
  undeliveredTo?: string[];
  /** Added 2026-09-17. The wire's `rct {mid, e, on}` folded per emoji, with
   *  the accounts that are on it — the count is `userIds.length` and whether
   *  your own toggle is on is `userIds.includes(me.id)`, so the row needs no
   *  second field for either. Optional because the stub does not fold them. */
  reactions?: Reaction[];
  /** On a transcript message with replies: the row under it. Derived by the
   *  store from the replies it holds, never carried on the wire. */
  thread?: ThreadSummary;
}

export interface Reaction {
  emoji: string;
  userIds: string[];
}

/** The desktop's `ThreadSummary`: what the row under a thread's root draws. */
export interface ThreadSummary {
  replyCount: number;
  /** The first three accounts to reply, in the order they first did. The
   *  first rather than the latest, so the stack does not reshuffle as the
   *  thread goes on. */
  participants: string[];
  /** When the newest reply was posted, milliseconds. */
  lastAt: number;
}

/** One thread, as the pane draws it. */
export interface ThreadView {
  /** Absent while it is not held: below what has loaded, or deleted. */
  root?: Message;
  /** Every reply held, oldest first. */
  replies: Message[];
  /** Its author deleted the root. The pane closes on it, as the desktop's
   *  does; the replies are still held and no longer drawn anywhere. */
  deleted: boolean;
}

// ── calls ───────────────────────────────────────────────────────────────────

/** One device in a call. The same seat the roster and `callstate` carry. */
export interface CallSeat {
  userId: string;
  deviceId: string;
  joinedAt: number;
}

/**
 * A call running in a conversation, as the conversation knows it.
 *
 * `since` is the first seat's `joinedAt`, held while any seat exists — never
 * the minimum of the current roster, or the clock jumps when whoever started it
 * leaves. An empty roster is **sent**, never implied, so `null` from
 * `callState()` means no call and an empty `roster` here never occurs.
 */
export interface CallState {
  conversationId: string;
  /** What the first joiner named it. `''` when a client too old to name one
   *  took the first seat. */
  callId: string;
  since: number;
  roster: CallSeat[];
}

/**
 * An ephemeral call frame, exactly as the desktop sends it.
 *
 * Posted inside `{t:'eph', c, payload}`, base64, capped at 2 KiB — and the cap
 * is fatal, not a drop: a payload over it closes the socket. `n` is a display
 * name nothing clamps, so the sender checks the length itself. Only `ring`
 * carries `cam` and `n`; the other three name an `id` the receiver already has.
 *
 * Call frames are the one ephemeral kind a client does **not** discard when it
 * comes from its own account. Answering on the phone is what stops the desktop
 * ringing, and nobody but you can say so.
 */
export interface CallFrame {
  v: 1;
  t: 'call';
  a: 'ring' | 'join' | 'decline' | 'leave';
  id: string;
  cam?: boolean;
  n?: string;
}

/**
 * A call frame as it arrived, whoever sent it.
 *
 * `IncomingCall` below is the `ring` half and is what a screen that draws an
 * incoming call wants. This is all four, and it exists for the other three:
 * a ring the caller gives up on, a `decline` from your own phone, a `leave`
 * from the last person in the room. Without them a ringing screen has no way
 * to be told to stop — `onIncomingCall` can only ever say that one started.
 *
 * `fromSelf` is your own account on another device, which is the case the
 * ephemeral lane's self-drop is deliberately widened for.
 */
export interface CallFrameEvent {
  conversationId: string;
  fromUserId: string;
  fromDeviceId: string;
  fromSelf: boolean;
  action: CallFrame['a'];
  callId: string;
  /** Only a `ring` carries these. */
  camera?: boolean;
  fromName?: string;
}

/** What `onIncomingCall` hands a listener. */
export interface IncomingCall {
  conversationId: string;
  callId: string;
  fromUserId: string;
  /** The caller's self-asserted display name from the `ring` frame. */
  fromName: string;
  camera: boolean;
}

// ── meetings ────────────────────────────────────────────────────────────────

export type MeetingStatus = 'scheduled' | 'live' | 'ended' | 'cancelled';

/** Host and cohost both satisfy `'host'` in a policy. */
export type MeetingRole = 'host' | 'cohost' | 'attendee';

/** `'host'`, `'everyone'`, or an explicit list of participant ids. */
export type Who = 'host' | 'everyone' | string[];

export interface MeetingPolicy {
  mic: Who;
  camera: Who;
  screen: Who;
  /** `strangers` is the default: the owner and the named invitees are seated on
   *  sight, everybody else arriving with the link knocks. `everyone` makes all
   *  but host and cohosts knock; `off` seats anyone holding the link. */
  lobby: 'strangers' | 'everyone' | 'off';
}

export interface MeetingInvitee {
  userId: string;
  role: MeetingRole;
}

/** The server's meeting row. */
export interface Meeting {
  id: string;
  ownerId: string;
  title: string;
  /** Milliseconds, or 0 for a meeting with no time set. */
  scheduledAt: number;
  status: MeetingStatus;
  invitees: MeetingInvitee[];
  /** The conversation it belongs to, or `''`. A meeting made from a chat
   *  carries it, and its invitees are that chat's members. */
  conversationId: string;
  policy: MeetingPolicy;
  /** Whether a recording is running. Every roster read shows it, which is what
   *  the consent banner is drawn from. */
  recording: boolean;
  /** Whether the signed-in account owns it. */
  own: boolean;
}

/** Where a party stands with a meeting. `none` is a party that has never
 *  presented itself — reading a standing never creates one. */
export type Admission = 'none' | 'knocking' | 'admitted' | 'denied';

export interface MeetingParticipant {
  /** An account id, or a `g_` guest id. The roster does not care which. */
  id: string;
  /** Self-asserted for a guest. Draw it through `participantLabel`. */
  displayName: string;
  guest: boolean;
  role: MeetingRole;
  /** Set by a host op, and the reason it is on the seat rather than in the
   *  local call session: everybody sees it, not just the muted person. */
  muted: boolean;
  admission: Admission;
}

/**
 * A participant's name as it may be shown, and the only way to show one.
 *
 * "(Guest)" beside a self-asserted name is the entire thing that stops a
 * stranger reading as somebody with an account. One function writes it so
 * there is one place to be right; a component that formats its own label is the
 * bug, and it has no symptom.
 */
export function participantLabel(p: { displayName: string; guest: boolean }): string {
  return p.guest ? `${p.displayName} (Guest)` : p.displayName;
}

// ── the store ───────────────────────────────────────────────────────────────

export interface SendOptions {
  /** The message this answers, if it is a thread reply. */
  replyTo?: string;
  /** A message to quote. The store resolves the excerpt. */
  quoteId?: string;
  /** Cloud node ids already listed by the picker. */
  cloudNodeIds?: string[];
  /** Account ids the composer's mention picker recorded. */
  mentions?: string[];
}

export type Unsubscribe = () => void;

/**
 * Everything the app knows, and everything it can do.
 *
 * One object, one `subscribe` — the store notifies on any change and callers
 * select what they care about through `useStore`. A per-collection subscription
 * API was the obvious first shape and is not worth it here: the data is small,
 * React bails out of a render when the selected value is unchanged, and one
 * listener list is one thing to get right.
 *
 * Every getter is **synchronous and returns what is known now**. A screen
 * renders from cache and the socket updates it; a getter that returned a
 * promise would make every component a loading state.
 */
export interface RelayStore {
  /** Called after any change. Returns the unsubscribe. */
  subscribe(listener: () => void): Unsubscribe;

  // reads
  /** The signed-in account. Null until `GET /api/relay/me` has answered. */
  me(): Person | null;
  conversations(): Conversation[];
  /** The transcript lane of a conversation: no reply whose root is held, and
   *  a `thread` summary on every root that has replies. */
  messages(conversationId: string): Message[];
  /** A thread: its root, its replies, and whether the root was deleted. */
  thread(conversationId: string, rootMessageId: string): ThreadView;
  /** The highest seq applied in a conversation, thread replies and edits
   *  included. What having it open reads up to: the desktop's `markRead`
   *  sends its cursor, not the last row of the transcript. */
  newestSeq(conversationId: string): number;
  /** Everything known about these accounts; unknown ids are simply absent. */
  people(ids: string[]): Person[];
  presenceOf(userId: string): Presence;
  /** Who is typing in a conversation, excluding yourself. */
  typing(conversationId: string): Person[];
  /** The running call, or null. */
  callState(conversationId: string): CallState | null;
  meetings(): Meeting[];
  /** Whether anything sits below the oldest message held — the question
   *  `loadOlder` answers for itself before it asks the server. A transcript
   *  that loads its own history needs it drawn out, because "nothing below
   *  this" is the one state in which it must stop watching its own ceiling. */
  hasOlder(conversationId: string): boolean;

  // writes
  /** Earlier history. Resolves when the range has landed, or immediately when
   *  the conversation is already at `availableFrom`. */
  loadOlder(conversationId: string): Promise<void>;
  /** Applies locally and renders now; the ack clears `pending`. Returns the
   *  message id, which a retry reuses. */
  send(conversationId: string, body: string, opts?: SendOptions): string;
  setTyping(conversationId: string, on: boolean): void;
  setPresence(presence: Presence): void;
  /** Exact match on a username or an email address, nothing else — a prefix
   *  search over the accounts table would be an enumeration service. */
  lookup(handleOrEmail: string): Promise<Person | null>;
  createConversation(
    kind: Exclude<ConversationKind, 'self'>,
    title: string,
    memberIds: string[]
  ): Promise<string>;
  addMember(conversationId: string, userId: string): Promise<void>;
  removeMember(conversationId: string, userId: string): Promise<void>;
  leave(conversationId: string): Promise<void>;
  /** The owner's delete-for-everyone. Unlike `leave`, which is your own exit,
   *  this takes the room down: every member's copy disappears, not just the
   *  caller's. */
  deleteForEveryone(conversationId: string): Promise<void>;
  rename(conversationId: string, title: string, topic?: string): Promise<void>;
  /** Monotonic — a lower seq is ignored by the server. */
  markRead(conversationId: string, seq: number): void;
  /** Replace the body of your own message — the wire's `edit`. Ignored for
   *  anybody else's, and for a body that is empty once trimmed. */
  editMessage(conversationId: string, messageId: string, body: string): void;
  /** Remove your own message — the wire's `del`. Ignored for anybody else's.
   *  An unacked one is retracted as well. */
  deleteMessage(conversationId: string, messageId: string): void;

  /**
   * The four writes the list's affordances make, added 2026-09-17.
   *
   * **Optional, and that is the whole reason they are here rather than
   * required.** `SampleRelayStore` holds none of these flags, and a required
   * method would have made this file's one job — being the seam nothing
   * reaches past — a reason to edit the stub instead. A caller writes
   * `store.setPinned?.(id, true)`: the affordance is drawn from the
   * conversation's own flag and does nothing until an implementation has one.
   */
  setPinned?(conversationId: string, pinned: boolean): void;
  setArchived?(conversationId: string, archived: boolean): void;
  setMuted?(conversationId: string, muted: boolean): void;
  /** Toggle an emoji on a message — the wire's `rct`. */
  react?(conversationId: string, messageId: string, emoji: string, on: boolean): void;

  // meetings
  createMeeting(opts: {
    title: string;
    scheduledAt?: number;
    conversationId?: string;
    invitees?: MeetingInvitee[];
    policy?: Partial<MeetingPolicy>;
  }): Promise<Meeting>;
  endMeeting(meetingId: string): Promise<void>;
  cancelMeeting(meetingId: string): Promise<void>;
  /** The guest link. Rotating it is what revokes the old one. */
  joinLink(meetingId: string, opts?: { rotate?: boolean }): Promise<string>;
  /** Who is waiting at the door. Empty for a meeting you do not host. */
  lobby(meetingId: string): MeetingParticipant[];
  admit(meetingId: string, partyId: string): Promise<void>;
  deny(meetingId: string, partyId: string): Promise<void>;

  // ringing
  /**
   * An incoming call, from a `ring` on the ephemeral lane.
   *
   * Ephemeral, so it reaches only a live socket — you cannot ring a closed
   * app, and that is correct rather than a gap. The durable record of a call
   * is the `callend` message, which is a different thing and arrives whether
   * anyone was connected or not.
   */
  onIncomingCall(cb: (call: IncomingCall) => void): Unsubscribe;
  /**
   * The four `a` values of `CallFrame`, one method each.
   *
   * `ring` names the call; every other frame names an id the receiver already
   * knows. The media side of all of this is `CallSession` in lib/calls — these
   * four only tell the other end what is happening.
   */
  ring(conversationId: string, callId: string, opts?: { camera?: boolean }): void;
  /** Sends `join`. */
  answer(conversationId: string, callId: string): void;
  /** Sends `decline`. The end that placed the call writes the durable
   *  `callend`; declining writes nothing, so one call leaves one row. */
  decline(conversationId: string, callId: string): void;
  /** Sends `leave`. */
  hangUp(conversationId: string, callId: string): void;
}
